import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";

import { useProcessManager } from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { api, type types } from "@/lib/openapi";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type Lane = types.Lane;

export const useLanesStore = defineStore("lanes", () => {
    const isFetching = ref(false);
    const { process } = useProcessManager();
    const { stek } = useStekManager();
    const lanes = ref<Lane[]>();
    const syncStore = useSyncStore();

    const controller = ref(new AbortController());

    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "process_definition"
                    )
                ) {
                    if (stek.value) {
                        loadLanesForProcess();
                    }
                }
            });
        }
    });

    async function loadLanesForProcess() {
        lanes.value = undefined;
        if (!process.value) {
            return;
        }
        controller.value.abort();
        controller.value = new AbortController();

        isFetching.value = true;
        const { response, data } =
            await api.startProcessLaneMgtGetLanesByProcessStartPost({
                body: {
                    process_id: process.value.identity,
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            if (response.status === 499) {
                // Request was aborted
                return;
            }
            throw new Error("Failed to load lanes");
        }

        lanes.value = data;
        isFetching.value = false;
    }

    watch(
        [() => process.value?.identity],
        () => {
            loadLanesForProcess();
        },
        { immediate: true }
    );

    return { lanes, isFetching };
});

export const useLanesManager = () => storeToRefs(useLanesStore());
